import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import API from "../Api";
import { PRODUCT_LOGIN_PATH } from "../utils/authRedirect";
import { createBillingCheckoutSession } from "../utils/billingCheckout";
import { clearSession, initSession, updateLastActivity } from "../utils/sessionManager";

const AuthContext = createContext(null);
const AUTH_ME_TIMEOUT_MS = 8000;

function parseAuthPayload(payload) {
  if (!payload || typeof payload !== "object" || payload.error) {
    return null;
  }

  const username = payload.username || payload.user?.username || payload.email || null;
  if (!username) return null;

  const role = payload.role || payload.user?.role || "USER";
  const workspaceId =
    payload.workspaceId ||
    payload.workspace?.id ||
    payload.workspace?.workspaceId ||
    null;
  const workspaceName =
    payload.workspaceName ||
    payload.workspace?.name ||
    null;
  const plan = payload.plan || payload.subscription?.plan || null;
  const trialEndsAt = payload.trialEndsAt || payload.subscription?.trialEndsAt || null;

  return {
    username,
    role,
    userId: payload.userId || payload.user?.id || null,
    email: payload.email || payload.user?.email || null,
    title: payload.title || payload.user?.title || null,
    workspaceId,
    workspaceName,
    workspace: payload.workspace || null,
    plan,
    trialEndsAt
  };
}

function persistSessionToken(payload) {
  if (payload?.token) {
    localStorage.setItem("token", payload.token);
  }
}

function mergeAuthInfo(prev, nextPlan, nextTrialEndsAt) {
  if (!prev) return prev;
  if (prev.plan === nextPlan && prev.trialEndsAt === nextTrialEndsAt) {
    return prev;
  }
  return { ...prev, plan: nextPlan, trialEndsAt: nextTrialEndsAt };
}

function isAuthorizedError(error) {
  const status = error?.response?.status;
  return status === 401 || status === 403;
}

function hasSessionCredential() {
  if (typeof window === "undefined") return false;
  if (localStorage.getItem("token")) return true;
  // Cookie-only sessions still need a probe, but keep it short-timeout.
  return document.cookie.split(";").some((c) => c.trim().startsWith("commray_auth="));
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authInfo, setAuthInfo] = useState(null);
  const [usage, setUsage] = useState(null);

  const refreshSubscription = useCallback(async () => {
    try {
      const res = await API.get("/api/billing/current", { timeout: 8000 });
      if (!res?.data) return null;
      const nextPlan = res.data.plan || res.data.subscription?.plan || null;
      const nextTrialEndsAt = res.data.trialEndsAt || res.data.subscription?.trialEndsAt || null;
      setAuthInfo((prev) => mergeAuthInfo(prev, nextPlan, nextTrialEndsAt));
      return res.data;
    } catch (error) {
      return null;
    }
  }, []);

  const refreshUsage = useCallback(async () => {
    try {
      const res = await API.get("/api/usage/current", { timeout: 8000 });
      setUsage(res?.data || null);
      return res?.data || null;
    } catch (error) {
      setUsage(null);
      return null;
    }
  }, []);

  const completeSessionBootstrap = useCallback(
    async (payload) => {
      const parsed = parseAuthPayload(payload);
      if (!parsed) {
        throw new Error("Invalid auth payload");
      }
      setAuthInfo(parsed);
      setIsAuthenticated(true);
      localStorage.setItem("username", parsed.username);
      localStorage.setItem("userRole", parsed.role || "USER");
      persistSessionToken(payload);
      updateLastActivity();
      initSession();
      // Never block UI on billing/usage, and never fan them out in parallel —
      // both endpoints resolve the same workspace and previously contended with Settings.
      refreshSubscription()
        .then(() => refreshUsage())
        .catch(() => {});
      return parsed;
    },
    [refreshSubscription, refreshUsage]
  );

  const bootstrapSession = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!hasSessionCredential()) {
        clearSession();
        setAuthInfo(null);
        setUsage(null);
        setIsAuthenticated(false);
        return null;
      }

      const meRes = await API.get("/auth/me", { timeout: AUTH_ME_TIMEOUT_MS });
      return await completeSessionBootstrap(meRes?.data);
    } catch (error) {
      try {
        if (!hasSessionCredential()) {
          throw error;
        }
        const legacyRes = await API.get("/auth/user-info", { timeout: AUTH_ME_TIMEOUT_MS });
        return await completeSessionBootstrap(legacyRes?.data);
      } catch (legacyError) {
        // Only wipe credentials on real auth failures. Soft timeouts previously cleared
        // the session while the pool was busy, which looked like a forced logout.
        if (isAuthorizedError(legacyError) || isAuthorizedError(error)) {
          clearSession();
        }
        setAuthInfo(null);
        setUsage(null);
        setIsAuthenticated(false);
        return null;
      }
    } finally {
      setIsLoading(false);
    }
  }, [completeSessionBootstrap]);

  const loginWithResponse = useCallback(
    async (payload) => {
      try {
        await completeSessionBootstrap(payload);
        return true;
      } catch {
        return false;
      }
    },
    [completeSessionBootstrap]
  );

  const logout = useCallback(async () => {
    try {
      await API.post("/auth/logout", {}, { timeout: 5000 });
    } catch (error) {
      // no-op: local cleanup below is authoritative client-side
    }
    clearSession();
    setUsage(null);
    setAuthInfo(null);
    setIsAuthenticated(false);
  }, []);

  const startCheckout = useCallback(async (plan = "starter") => {
    const readCheckoutUrl = (data) =>
      data?.url || data?.checkoutUrl || data?.sessionUrl || null;

    try {
      const res = await API.post("/api/billing/change-plan", { plan });
      const checkoutUrl = readCheckoutUrl(res?.data);
      if (checkoutUrl) {
        if (typeof window !== "undefined") {
          window.location.assign(checkoutUrl);
        }
        return res.data;
      }
      await refreshSubscription();
      if (res?.data?.alreadyActive || res?.data?.updated || res?.data?.scheduled || res?.data?.message) {
        return res.data;
      }
      throw new Error(res?.data?.error || "Checkout URL was not returned by billing API");
    } catch (productApiError) {
      if (productApiError?.response) {
        throw new Error(
          productApiError.response.data?.error ||
            productApiError.response.data?.message ||
            "Plan change failed"
        );
      }
      const billingRes = await createBillingCheckoutSession(plan);
      const checkoutUrl = readCheckoutUrl(billingRes?.data);
      if (!checkoutUrl) {
        throw new Error(billingRes?.data?.error || "Checkout URL was not returned by billing API");
      }
      if (typeof window !== "undefined") {
        window.location.assign(checkoutUrl);
      }
      return billingRes.data;
    }
  }, [refreshSubscription]);

  const hasActiveTrial =
    !!authInfo?.trialEndsAt && new Date(authInfo.trialEndsAt).getTime() > Date.now()
    && String(authInfo?.plan || "").toLowerCase() === "trial";
  const planName = authInfo?.plan || (hasActiveTrial ? "trial" : "free");
  const paidPlans = new Set(["starter", "team", "growth", "enterprise", "pro"]);
  const hasPaidPlan = paidPlans.has(String(planName).toLowerCase());
  const canUsePremium = hasPaidPlan || hasActiveTrial;

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      authInfo,
      usage,
      planName,
      hasActiveTrial,
      canUsePremium,
      loginRedirectUrl: PRODUCT_LOGIN_PATH,
      bootstrapSession,
      loginWithResponse,
      logout,
      refreshSubscription,
      refreshUsage,
      startCheckout
    }),
    [
      isAuthenticated,
      isLoading,
      authInfo,
      usage,
      planName,
      hasActiveTrial,
      canUsePremium,
      bootstrapSession,
      loginWithResponse,
      logout,
      refreshSubscription,
      refreshUsage,
      startCheckout
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export { isAuthorizedError };
